import { TFunction } from "i18next";
import { Country, LegalForm } from "@/api/data-contracts";
import { CommonI18N } from "@/translations";

/**
 * Returns the legal forms selectable for the given country, based on the given country-to-legal-form
 * reference map (as returned by useLegalFormsByCountry). Returns an empty list when the country is not
 * set (e.g. not yet chosen in a form) or the map has no entry for it (e.g. the reference data has not
 * finished loading yet) — never throws.
 *
 * @param {Country | undefined} country - The country to look up.
 * @param {Record<string, LegalForm[]>} map - The country-to-legal-form reference map.
 * @returns {LegalForm[]} The legal forms allowed for the given country, or an empty list.
 */
export function allowedLegalForms(country: Country | undefined, map: Record<string, LegalForm[]>): LegalForm[] {
    if (!country) {
        return [];
    }
    return map[country] ?? [];
}

/**
 * Checks whether the given legal form is selectable for the given country, based on the given
 * country-to-legal-form reference map.
 *
 * @param {Country | undefined} country - The country to check against.
 * @param {LegalForm | undefined} legalForm - The legal form to check.
 * @param {Record<string, LegalForm[]>} map - The country-to-legal-form reference map.
 * @returns {boolean} True if the combination is allowed, false otherwise (including when either input is missing).
 */
export function isLegalFormAllowed(
    country: Country | undefined,
    legalForm: LegalForm | undefined,
    map: Record<string, LegalForm[]>
): boolean {
    if (!legalForm) {
        return false;
    }
    return allowedLegalForms(country, map).includes(legalForm);
}

/**
 * Returns the legal-form options to offer for the given country, with one addition for the passive legacy
 * display case: when a legal form is already stored on the model but is NOT among the country's allowed
 * forms (e.g. a country change since the last save, or data predating this feature), the stored value is
 * appended as a transient out-of-list option so a plain view can still render it — without ever mutating
 * the model or persisting anything. A stored value that IS already allowed is never duplicated.
 *
 * @param {Country | undefined} country - The country to filter by.
 * @param {LegalForm | undefined} storedLegalForm - The legal form currently held on the model, possibly
 *   out-of-list for the given country (the legacy case this function exists for).
 * @param {Record<string, LegalForm[]>} map - The country-to-legal-form reference map.
 * @returns {LegalForm[]} The country's allowed legal forms, with the out-of-list stored value appended at
 *   the end when applicable; never contains duplicates.
 */
export function legalFormOptionsForDisplay(
    country: Country | undefined,
    storedLegalForm: LegalForm | undefined,
    map: Record<string, LegalForm[]>
): LegalForm[] {
    const allowed = allowedLegalForms(country, map);
    if (storedLegalForm && !allowed.includes(storedLegalForm)) {
        return [...allowed, storedLegalForm];
    }
    return allowed;
}

/**
 * Decides whether changing the company address's country to the given (new) country needs the operator
 * to re-pick a legal form before saving: true when a legal form is already stored AND it is NOT allowed
 * for the (new) country. Returns false when there is no stored legal form (nothing to reconcile) or when
 * the stored legal form is still allowed for the (new) country (including the cross-country SE/Sonstige
 * fallback) — a plain, testable decision with no side effects; it does not decide what happens next (that
 * is the caller's job — open a follow-up dialog, reject, etc.).
 *
 * @param {Country | undefined} country - The (new) country to check the stored legal form against.
 * @param {LegalForm | undefined} legalForm - The legal form currently stored on the company.
 * @param {Record<string, LegalForm[]>} map - The country-to-legal-form reference map.
 * @returns {boolean} True if a stored legal form exists and is not allowed for the given country.
 */
export function needsLegalFormReselection(
    country: Country | undefined,
    legalForm: LegalForm | undefined,
    map: Record<string, LegalForm[]>
): boolean {
    return !!legalForm && !isLegalFormAllowed(country, legalForm, map);
}

/**
 * Decides whether saving a company-address edit should open the guided legal-form follow-up dialog: true
 * only when the country actually changed (compared to the originally loaded address) AND the newly chosen
 * country needs a legal-form reselection (see needsLegalFormReselection). A legacy company whose stored
 * combination is already incompatible must remain saveable for a plain address correction (e.g. a street
 * fix) as long as the country stays the same — the guided dialog (and the server-side reject fallback) only
 * ever fires on a genuine country change.
 *
 * @param {Country | undefined} originalCountry - The country originally loaded onto the address (the
 *   unmodified starting value, before any edits in the current dialog session).
 * @param {Country | undefined} newCountry - The country currently selected in the edit form.
 * @param {LegalForm | undefined} legalForm - The legal form currently stored on the company.
 * @param {Record<string, LegalForm[]>} map - The country-to-legal-form reference map.
 * @returns {boolean} True if the country actually changed and the new combination needs reselection.
 */
export function needsLegalFormReselectionForCountryChange(
    originalCountry: Country | undefined,
    newCountry: Country | undefined,
    legalForm: LegalForm | undefined,
    map: Record<string, LegalForm[]>
): boolean {
    const countryChanged = originalCountry !== newCountry;
    return countryChanged && needsLegalFormReselection(newCountry, legalForm, map);
}

/**
 * Builds sorted `{label, value}` select options for the given legal forms, translating each via the
 * shared CommonI18N.enum.legalForm translation keys and sorting alphabetically by the translated (German) label.
 * Shared by every place that renders a legal-form select (the company details page's inline field and the
 * guided-dialog's follow-up select) so the sort/translate logic is not duplicated per call site.
 *
 * @param {LegalForm[]} legalForms - The legal forms to turn into select options.
 * @param {TFunction} t - The i18next translation function used to resolve each form's display label.
 * @returns {{label: string, value: LegalForm}[]} The translated options, sorted by label (German locale).
 */
export function toSortedLegalFormOptions(
    legalForms: LegalForm[],
    t: TFunction
): { label: string; value: LegalForm }[] {
    return legalForms
        .map((legalForm) => ({ label: t(CommonI18N.enum.legalForm[legalForm]), value: legalForm }))
        .sort((a, b) => a.label.localeCompare(b.label, "de", { sensitivity: "base" }));
}
